import { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, ChevronUp, ChevronDown, Trash2, MapPin } from 'lucide-react';
import { TargetCostRanges } from '@/types/target-cost';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/lib/utils';
import { useRangeCalculations } from '@/hooks/useRangeCalculations';

interface RangeFormProps {
  ranges: TargetCostRanges;
  onChange: (updates: Partial<TargetCostRanges>) => void;
  targetPrice?: number;
  actualCostPct?: number;
}

interface NumberInputWithButtonsProps {
  value: number;
  onChange: (value: string) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  min?: number;
  max?: number;
  readOnly?: boolean;
}

function NumberInputWithButtons({
  value,
  onChange,
  onIncrement,
  onDecrement,
  min = 0,
  max = 1000,
  readOnly = false,
}: NumberInputWithButtonsProps) {
  if (readOnly) {
    return <Input value={value} readOnly className="w-24 text-right bg-muted/50" />;
  }

  return (
    <div className="relative">
      <Input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-24 text-right pr-8"
        min={min}
        max={max}
      />
      <div className="absolute right-1 top-1 bottom-1 flex flex-col">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-4 w-4 p-0"
          onClick={onIncrement}
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-4 w-4 p-0"
          onClick={onDecrement}
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export function RangeForm({
  ranges,
  onChange,
  targetPrice,
  actualCostPct,
}: RangeFormProps) {
  const {
    totals,
    handleFixedRangeChange,
    handleDynamicRangeChange,
    addDynamicRange,
    removeDynamicRange,
  } = useRangeCalculations({
    ranges,
    onChange,
    targetPrice,
    actualCostPct,
  });

  // Determine active ranges based on amountInRange > 0
  const isLessThanActive = useMemo(() => totals.lessThanAmount > 0, [totals.lessThanAmount]);
  const isGreaterThanActive = useMemo(() => totals.greaterThanAmount > 0, [totals.greaterThanAmount]);

  // Check if dynamic range is active (has non-zero amountInRange)
  const isDynamicRangeActive = useMemo(() => {
    return (rangeId: string) => {
      const range = ranges.dynamicRanges.find(r => r.id === rangeId);
      return range ? range.amountInRange > 0 : false;
    };
  }, [ranges.dynamicRanges]);

  // Show current position indicator when we have valid cost data
  const showPositionIndicator = actualCostPct !== undefined && actualCostPct > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Pain/Gain Share Ranges
          {showPositionIndicator && (
            <span className="flex items-center gap-1.5 text-sm font-normal text-muted-foreground">
              <MapPin className="h-4 w-4" />
              Current position: {actualCostPct.toFixed(1)}%
            </span>
          )}
        </CardTitle>
        {showPositionIndicator && (
          <CardDescription>
            Active ranges are highlighted in blue
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="relative overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left">Range</th>
                <th className="px-4 py-3 text-right">From (%)</th>
                <th className="px-4 py-3 text-right">To (%)</th>
                <th className="px-4 py-3 text-right">Share (%)</th>
                <th className="px-4 py-3 text-right">Amount in Range</th>
                <th className="px-4 py-3 text-right">Contractor's Share</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {/* Less Than Range */}
              <tr className={cn(
                "border-b",
                isLessThanActive
                  ? "bg-blue-50 dark:bg-blue-950/30 ring-1 ring-inset ring-blue-200 dark:ring-blue-800"
                  : "bg-muted/30"
              )}>
                <td className="px-4 py-3">Less Than</td>
                <td className="px-4 py-3 text-right">-</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <NumberInputWithButtons
                      value={ranges.lessThanRange.percentage}
                      onChange={(v) =>
                        handleFixedRangeChange('less-than', 'percentage', v)
                      }
                      onIncrement={() =>
                        handleFixedRangeChange(
                          'less-than',
                          'percentage',
                          (ranges.lessThanRange.percentage + 1).toString()
                        )
                      }
                      onDecrement={() =>
                        handleFixedRangeChange(
                          'less-than',
                          'percentage',
                          (ranges.lessThanRange.percentage - 1).toString()
                        )
                      }
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <NumberInputWithButtons
                      value={ranges.lessThanRange.sharePercentage}
                      onChange={(v) =>
                        handleFixedRangeChange('less-than', 'sharePercentage', v)
                      }
                      onIncrement={() =>
                        handleFixedRangeChange(
                          'less-than',
                          'sharePercentage',
                          (ranges.lessThanRange.sharePercentage + 1).toString()
                        )
                      }
                      onDecrement={() =>
                        handleFixedRangeChange(
                          'less-than',
                          'sharePercentage',
                          (ranges.lessThanRange.sharePercentage - 1).toString()
                        )
                      }
                      max={100}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(totals.lessThanAmount)}
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(totals.lessThanShare)}
                </td>
                <td className="px-4 py-3"></td>
              </tr>

              {/* Dynamic Ranges */}
              {ranges.dynamicRanges.map((range, index) => (
                <tr
                  key={range.id}
                  className={cn(
                    'border-b',
                    isDynamicRangeActive(range.id)
                      ? "bg-blue-50 dark:bg-blue-950/30 ring-1 ring-inset ring-blue-200 dark:ring-blue-800"
                      : index % 2 === 0 ? 'bg-muted/30' : 'bg-muted/10'
                  )}
                >
                  <td className="px-4 py-3">Range {index + 1}</td>
                  <td className="px-4 py-3 text-right">
                    <NumberInputWithButtons
                      value={range.startPercentage}
                      onChange={() => {}}
                      onIncrement={() => {}}
                      onDecrement={() => {}}
                      readOnly
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <NumberInputWithButtons
                        value={range.endPercentage}
                        onChange={(v) =>
                          handleDynamicRangeChange(range.id, 'endPercentage', v)
                        }
                        onIncrement={() =>
                          handleDynamicRangeChange(
                            range.id,
                            'endPercentage',
                            (range.endPercentage + 1).toString()
                          )
                        }
                        onDecrement={() =>
                          handleDynamicRangeChange(
                            range.id,
                            'endPercentage',
                            (range.endPercentage - 1).toString()
                          )
                        }
                        min={range.startPercentage}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <NumberInputWithButtons
                        value={range.sharePercentage}
                        onChange={(v) =>
                          handleDynamicRangeChange(range.id, 'sharePercentage', v)
                        }
                        onIncrement={() =>
                          handleDynamicRangeChange(
                            range.id,
                            'sharePercentage',
                            (range.sharePercentage + 1).toString()
                          )
                        }
                        onDecrement={() =>
                          handleDynamicRangeChange(
                            range.id,
                            'sharePercentage',
                            (range.sharePercentage - 1).toString()
                          )
                        }
                        max={100}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCurrency(range.amountInRange)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCurrency(range.contractorShare)}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDynamicRange(range.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}

              {/* Greater Than Range */}
              <tr className={cn(
                "border-b",
                isGreaterThanActive
                  ? "bg-blue-50 dark:bg-blue-950/30 ring-1 ring-inset ring-blue-200 dark:ring-blue-800"
                  : "bg-muted/30"
              )}>
                <td className="px-4 py-3">Greater Than</td>
                <td className="px-4 py-3 text-right">
                  <NumberInputWithButtons
                    value={ranges.greaterThanRange.percentage}
                    onChange={() => {}}
                    onIncrement={() => {}}
                    onDecrement={() => {}}
                    readOnly
                  />
                </td>
                <td className="px-4 py-3 text-right">-</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <NumberInputWithButtons
                      value={ranges.greaterThanRange.sharePercentage}
                      onChange={(v) =>
                        handleFixedRangeChange('greater-than', 'sharePercentage', v)
                      }
                      onIncrement={() =>
                        handleFixedRangeChange(
                          'greater-than',
                          'sharePercentage',
                          (ranges.greaterThanRange.sharePercentage + 1).toString()
                        )
                      }
                      onDecrement={() =>
                        handleFixedRangeChange(
                          'greater-than',
                          'sharePercentage',
                          (ranges.greaterThanRange.sharePercentage - 1).toString()
                        )
                      }
                      max={100}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(totals.greaterThanAmount)}
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(totals.greaterThanShare)}
                </td>
                <td className="px-4 py-3"></td>
              </tr>

              {/* Totals */}
              <tr className="bg-primary/5 font-medium">
                <td colSpan={4} className="px-4 py-3">
                  Total
                </td>
                <td className="px-4 py-3 text-right">
                  {formatCurrency(totals.totalAmountInRange)}
                </td>
                <td className="px-4 py-3 text-right">
                  {formatCurrency(totals.totalContractorShare)}
                </td>
                <td className="px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <Button variant="outline" onClick={addDynamicRange} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Range
        </Button>
      </CardContent>
    </Card>
  );
}
